import { useCallback, useEffect, useMemo, useRef } from "react";
import IconButton from "rsuite/IconButton";
import ButtonGroup from "rsuite/ButtonGroup";
import Slider from "rsuite/Slider";
import FlexboxGrid from "rsuite/FlexboxGrid";
import PauseIcon from "@rsuite/icons/legacy/Pause";
import NextIcon from "@rsuite/icons/legacy/PageNext";
import PrevIcon from "@rsuite/icons/legacy/PagePrevious";
import PlayIcon from "@rsuite/icons/legacy/Play";
import StopIcon from "@rsuite/icons/legacy/Stop";
import { Capitalize } from "../utils";
import TWEEN from "@tweenjs/tween.js";
import useKeypress from "react-use-keypress";
import { isMobile } from "react-device-detect";
import { useWindowResize } from "../hooks/useWindowResize";

// Hoisted static styles
const VOLUME_CONTAINER_STYLE = {
  width: 100,
  position: "absolute",
  bottom: 15,
  left: 15,
};

const BUTTON_GROUP_STYLE = {
  filter: "drop-shadow(0px 1px 18px #000000)",
};

const TRACK_COUNTER_STYLE = {
  textAlign: "center",
};

const BUTTON_GROUP_CONTAINER_STYLE = {
  position: "absolute",
  top: 15,
  left: 15,
};

const FLEX_GRID_ITEM_STYLE = {
  pointerEvents: "none",
};

const TITLE_CONTENT_STYLE = {
  fontFamily: "Kdam Thmor Pro",
  textAlign: "center",
  margin: 50,
  padding: 20,
  pointerEvents: "none",
};

const TITLE_H4_STYLE = {
  color: "#333",
  fontSize: 40,
  fontFamily: "Permanent Marker",
  filter: "drop-shadow(0px 0px 5px #17467aAA)",
};

const AUTHOR_STYLE = {
  fontFamily: "Lobster",
  fontSize: 25,
  color: "#555555",
  filter: "drop-shadow(0px 0px 5px #FFFFFF88)",
  fontStyle: "italic",
};

const OCTETS_STYLE = {
  color: "#333",
  filter: "drop-shadow(0px 0px 2px #000000EE)",
};

function PlayerControl({
  player,
  isPlay,
  setIsPlay,
  volume,
  setVolume,
  togglePlay,
  currentTrack,
  meta,
  size,
  isNextTrack,
  isPrevTrack,
  nextTrack,
  prevTrack,
  lengthTracks,
  isMouseMoving,
  isLoading,
}) {
  const FlexContent = useRef();
  const titlePanel = useRef();
  const bottomTitle = useRef();
  const topTitle = useRef();
  const tweenAnimRef = useRef();

  const handleResize = useCallback(() => {
    if (FlexContent.current) {
      FlexContent.current.style.height = `${window.innerHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      if (bottomTitle.current) {
        bottomTitle.current.style.opacity = 0;
      }
      if (titlePanel.current) {
        titlePanel.current.style.opacity = 0;
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (tweenAnimRef.current) TWEEN.remove(tweenAnimRef.current);

    if (bottomTitle.current) {
      bottomTitle.current.style.opacity = 0;
    }
    if (titlePanel.current && isPlay) {
      if (isMobile) {
        titlePanel.current.style.display = "none";
      }
      titlePanel.current.style.opacity = 0;
      const timeAnim = 250;
      tweenAnimRef.current = new TWEEN.Tween(titlePanel.current.style)
        .to({ opacity: 1 }, timeAnim)
        .delay(2000)
        .easing(TWEEN.Easing.Sinusoidal.InOut)
        .onComplete(() => {
          tweenAnimRef.current = new TWEEN.Tween(titlePanel.current.style)
            .to({ opacity: 0 }, timeAnim)
            .delay(2000)
            .easing(TWEEN.Easing.Sinusoidal.InOut)
            .onComplete(() => {
              tweenAnimRef.current = new TWEEN.Tween(bottomTitle.current.style)
                .to({ opacity: 0.85 }, timeAnim / 2)
                .easing(TWEEN.Easing.Sinusoidal.InOut)
                .start();
            })
            .start();
        })
        .start();
    }
  }, [meta, isPlay]);

  useWindowResize(handleResize);

  const title = useMemo(() => {
    return (
      `${currentTrack.pos}. ` +
      String(meta.title ? meta.title : currentTrack.filename).toUpperCase()
    );
  }, [currentTrack, meta]);

  const authors = useMemo(() => {
    const a = currentTrack.author;
    const authorsList = [];
    for (let c in a) {
      authorsList.push(Capitalize(a[c]));
    }

    return authorsList.join(" & ");
  }, [currentTrack]);

  const octets = useMemo(() => {
    return size.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }, [size]);

  useKeypress(" ", togglePlay);
  useKeypress("ArrowLeft", prevTrack);
  useKeypress("ArrowRight", nextTrack);
  useKeypress("ArrowUp", () => {
    const newVolume = volume + 5;
    setVolume(newVolume <= 100 ? newVolume : 100);
  });
  useKeypress("ArrowDown", () => {
    const newVolume = volume - 5;
    setVolume(newVolume > 0 ? newVolume : 0);
  });

  return (
    <>
      {!isMobile && (
        <div
          style={VOLUME_CONTAINER_STYLE}
          className={!isMouseMoving ? "hide" : ""}
        >
          <label>Volume </label>

          <Slider
            progress={true}
            defaultValue={volume}
            value={volume}
            onChange={(value) => {
              setVolume(value);
            }}
          />
        </div>
      )}

      <div
        ref={bottomTitle}
        style={{
          width: window.innerWidth,
          position: "absolute",
          fontFamily: "Kdam Thmor Pro",
          bottom: 15,
          left: 0,
          opacity: 0,
          textAlign: "center",
          filter: "drop-shadow(0px 0px 5px #000000FF)",
          fontSize: 15,
          color: "#FFF",
          pointerEvents: "none",
        }}
      >
        {isMobile ? (
          <>
            <b>{title}</b> by {authors} <br />
            in {currentTrack.year} <br />
            {octets} octets
          </>
        ) : (
          <>
            <b>{title}</b> by {authors} in {currentTrack.year} (
            {octets} octets)
          </>
        )}
      </div>

      {!isMobile && (
        <div
          ref={topTitle}
          className={!isMouseMoving ? "hide" : ""}
          style={{
            width: window.innerWidth,
            position: "absolute",
            fontFamily: "Kdam Thmor Pro",
            top: 15,
            left: 0,
            textAlign: "center",
            filter: "drop-shadow(0px 0px 5px #000000FF)",
            fontSize: 15,
            color: "#FFF",
            pointerEvents: "none",
          }}
        >
          Change track <b>← →</b> - Volume <b>↑ ↓</b> - use keyboard to display
          information <b>(i)</b> and playlist <b>(p)</b>
        </div>
      )}

      <div
        style={BUTTON_GROUP_CONTAINER_STYLE}
        className={!isMouseMoving ? "hide" : ""}
      >
        <ButtonGroup
          size="sm"
          style={BUTTON_GROUP_STYLE}
        >
          <IconButton
            icon={<PrevIcon />}
            placement="left"
            disabled={!isPrevTrack}
            onClick={prevTrack}
          />
          {isPlay ? (
            <IconButton
              icon={<PauseIcon />}
              placement="left"
              onClick={() => togglePlay()}
            />
          ) : (
            <IconButton
              icon={<PlayIcon />}
              placement="left"
              onClick={() => togglePlay()}
            />
          )}

          <IconButton
            icon={<StopIcon />}
            placement="left"
            onClick={() => {
              player.seek(0);
              player.pause();
              setIsPlay(false);
            }}
          />

          <IconButton
            icon={<NextIcon />}
            placement="left"
            disabled={!isNextTrack}
            onClick={nextTrack}
          />
        </ButtonGroup>
        <br />
        <div style={TRACK_COUNTER_STYLE}>
          {currentTrack.pos} / {lengthTracks}
        </div>
      </div>

      <FlexboxGrid
        // justify="space-around"
        align="middle"
        ref={FlexContent}
        style={{
          display: "flex",
          height: window.innerHeight,
          pointerEvents: "none",
        }}
      >
        <FlexboxGrid.Item colspan={10} style={FLEX_GRID_ITEM_STYLE}>
          <div
            ref={titlePanel}
            style={{
              opacity: 0,
              width: window.innerWidth,
              backdropFilter: "blur(16px) brightness(120%)",
              filter: "drop-shadow(0px 0px 5px #FFFFFF88)",
              border: "5px solid rgba(255, 255, 255, 0.15)",
              borderLeft: "none",
              borderRight: "none",
              background: "rgba(255,255,255, 0.05)",
            }}
          >
            <div style={TITLE_CONTENT_STYLE}>
              <h4 style={TITLE_H4_STYLE}>
                {title}
              </h4>
              <b style={AUTHOR_STYLE}>
                by {authors} in {currentTrack.year}
              </b>
              <br />
              <p style={OCTETS_STYLE}>
                {octets} octets
              </p>
            </div>
          </div>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </>
  );
}

export default PlayerControl;
